import type React from 'react';
import { memo } from 'react';

import { Divider, Loading, Tooltip, Typography } from '@cobliteam/ui/components';
import moment from 'moment-timezone';

import { MediaType } from '@cobliteam/common-types';

import Icon from '@cobliteam/ui/icons';

import { DeviceType } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useVehicle } from '@cobliteam/hooks';
import RadioGroupItem, { RadioGroup } from '@cobliteam/ui/form/Radio';
import { cn } from '@cobliteam/ui/utils';
import useMediaStore from '../../../../store/zustand/useMediaStore';

interface ButtonWrapperProps {
  isMulticam: boolean;
  children: React.ReactNode;
}

const TimelapseWrapper = ({ isMulticam, children }: ButtonWrapperProps) => {
  if (isMulticam) {
    return (
      <Tooltip align='end' content='Este formato não está disponível para dispositivos com mais de 2 canais de vídeo, como a Coblicam Multi.'>
        {children}
      </Tooltip>
    );
  }

  return children;
};

export const MediaTypeSelectionStep: React.FC = memo(() => {
  const mediaRequestData = useMediaStore((state) => state.requestMediaData);
  const setMediaRequestData = useMediaStore((state) => state.setRequestMediaData);

  const { getVehicle } = useVehicle();
  const { data: vehicle, isLoading } = getVehicle(mediaRequestData.vehicleId!, {});
  const isMulticam = vehicle?.deviceType === DeviceType.Multicam;

  const onChangeOptionTimelapse = () => {
    setMediaRequestData({
      ...mediaRequestData,
      mediaType: MediaType.TIMELAPSE,
      requestMinute: undefined,
    });
  };

  const onChangeOptionFootage = () => {
    let requestMinute;

    if (mediaRequestData?.requestPeriodStart) {
      const startTimeMoment = moment(mediaRequestData.requestPeriodStart);
      const hour = startTimeMoment.hour();
      const minute = startTimeMoment.minute();

      requestMinute = `${hour < 10 ? `0${hour}` : hour}:${minute < 10 ? `0${minute}` : minute}`;
    }

    setMediaRequestData({
      ...mediaRequestData,
      mediaType: MediaType.FOOTAGE_VIDEO,
      requestMinute,
    });
  };

  if (isLoading) {
    return (
      <div className='flex h-full w-full content-center justify-center'>
        <Loading />
      </div>
    );
  }

  return (
    <div className='h-full w-full px-7 py-6'>
      <div className='mb-3 flex gap-1'>
        <Icon name='video' color='primary' />
        <Typography variant='description-title'>Escolher categoria do vídeo</Typography>
      </div>
      <Divider />
      <RadioGroup className='mt-10 flex w-full justify-around'>
        <div
          className='h-64 w-[360px] cursor-pointer rounded border border-gray-light p-5 hover:scale-[1.01] hover:border-primary hover:bg-primary-2 hover:duration-100'
          onClick={onChangeOptionFootage}
        >
          <div className='mb-5 flex items-center gap-3'>
            <RadioGroupItem checked={mediaRequestData?.mediaType === MediaType.FOOTAGE_VIDEO} value={MediaType.FOOTAGE_VIDEO} />
            <Typography variant='menu-default'>Vídeo de um trecho (até 1 minuto)</Typography>
          </div>
          <video loop autoPlay muted controls={false} className='w-80'>
            <source src='https://static.cobli.co/videos/videoMinuto1.mp4' type='video/mp4' />
          </video>
        </div>
        <TimelapseWrapper isMulticam={isMulticam}>
          <div
            className={cn(
              'h-64 w-[360px] rounded border border-gray-light p-5',
              isMulticam ? 'cursor-not-allowed bg-gray-light' : 'cursor-pointer hover:scale-[1.01] hover:border-primary hover:bg-primary-2 hover:duration-100'
            )}
            onClick={isMulticam ? undefined : onChangeOptionTimelapse}
          >
            <div className='mb-5 flex items-center gap-3'>
              <RadioGroupItem checked={mediaRequestData?.mediaType === MediaType.TIMELAPSE} value={MediaType.TIMELAPSE} disabled={isMulticam} />
              <Typography variant='menu-default' className={isMulticam ? 'text-gray' : ''}>
                Resumo do trajeto (time-lapse)
              </Typography>
            </div>
            <video loop autoPlay muted controls={false} className='w-80'>
              <source src='https://static.cobli.co/videos/videoTimelapse2.mp4' type='video/mp4' />
            </video>
          </div>
        </TimelapseWrapper>
      </RadioGroup>
    </div>
  );
});

export default MediaTypeSelectionStep;
